/**
 * OverlaySpec — the optional, versioned `overlay` sublayer of the M4 MapSpec (M9).
 *
 * Like the M8 `analysis` block, it rides INSIDE the MapSpec (persisted in
 * `map.style` / the M6 snapshot), so a saved/embedded map redraws its census
 * overlay deterministically through the one-spec contract — never a parallel
 * representation. A MapSpec with no `overlay` block renders byte-identically to
 * before it existed. The block carries only the SELECTION (source, country,
 * variable, tier, classification, ramp token, opacity); the boundary geometry +
 * values are fetched from the overlay engine at render time, keyed by this block.
 *
 * Versioning is on the block itself (`overlay.version`), independent of
 * MAPSPEC_VERSION: a v(N-1) overlay block migrates up under vN via the migration
 * registry below, exactly like the top-level MapSpec + the analysis block.
 */

import { z } from "zod";
import { OVERLAY_COUNTRIES, OVERLAY_SOURCES, CLASSIFICATION_METHODS } from "./types";

export const OVERLAY_VERSION = 1;

export const CountrySchema = z.enum(OVERLAY_COUNTRIES);
export const OverlaySourceSchema = z.enum(OVERLAY_SOURCES);
export const ClassificationMethodSchema = z.enum(CLASSIFICATION_METHODS);

/** Persisted classification: the method + (for `manual`) the explicit breaks. */
export const OverlayClassificationSchema = z.object({
  method: ClassificationMethodSchema,
  /** Class count for quantile/equal (ignored for manual). */
  classes: z.number().int().positive().optional(),
  /** Explicit ascending break edges — required when method === "manual". */
  breaks: z.array(z.number()).optional(),
});
export type OverlayClassification = z.infer<typeof OverlayClassificationSchema>;

/**
 * The persisted `overlay` block (M9 interface `map.style.overlay`). Consumed by
 * M4 (render), M6 (share/embed redraw), M7 (gate check on read).
 */
export const OverlaySpecSchema = z.object({
  version: z.literal(OVERLAY_VERSION),
  source: OverlaySourceSchema,
  country: CountrySchema,
  variableId: z.string(),
  tier: z.string(),
  classification: OverlayClassificationSchema,
  /**
   * Ramp FAMILY token id (e.g. `map.ramp.sequential`) — TOKEN-KEYED, never a raw
   * hex. Resolved to concrete stop colors at render time from the token artifact.
   */
  rampTokenId: z.string(),
  /** Fill opacity (0..1). */
  opacity: z.number().min(0).max(1),
  /** Optional data vintage pin; absent = the engine's current vintage. */
  vintage: z.string().optional(),
});
export type OverlaySpec = z.infer<typeof OverlaySpecSchema>;

/** Validate an unknown value as a current-version OverlaySpec. */
export function parseOverlay(input: unknown): OverlaySpec {
  return OverlaySpecSchema.parse(input);
}

/**
 * Overlay migration registry, keyed by the version being migrated FROM. A
 * pre-versioned block (no `version` field) is treated as v0 and adopted under v1;
 * content-reshaping migrations register here as OVERLAY_VERSION climbs.
 */
type OverlayMigration = (o: Record<string, unknown>) => Record<string, unknown>;
const OVERLAY_MIGRATIONS: Record<number, OverlayMigration> = {
  0: (o) => ({ ...o, version: OVERLAY_VERSION }),
};

/**
 * Migrate an overlay block of any supported prior version up to the current
 * version, then validate. Exported so `migrateMapSpec` can pre-migrate the nested
 * block before the strict MapSpec validator (which pins `overlay.version` to the
 * literal) runs. A current-version block passes straight through; an absent block
 * is never passed here.
 */
export function migrateOverlay(input: unknown): OverlaySpec {
  if (typeof input !== "object" || input === null) {
    throw new Error("overlay must be an object");
  }
  let o = input as Record<string, unknown>;
  let version = typeof o.version === "number" ? o.version : 0;
  if (version > OVERLAY_VERSION) {
    throw new Error(`overlay version ${version} is newer than supported (${OVERLAY_VERSION})`);
  }
  while (version < OVERLAY_VERSION) {
    const migrate = OVERLAY_MIGRATIONS[version];
    if (!migrate) throw new Error(`No migration from overlay version ${version}`);
    o = migrate(o);
    version = o.version as number;
  }
  return OverlaySpecSchema.parse(o);
}
